import { useMemo, useState } from "react";
import { Do } from "@rsm-hcd/javascript-core";
import type PublicationRecord from "models/view-models/publication-record";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import { useLocalization } from "utilities/hooks/use-localization";
import { ToastManager } from "utilities/toast/toast-manager";
import UserPublicationPinRecord from "models/view-models/user-publication-pin-record";
import UserPublicationPinService from "utilities/services/user-publication-pins/user-publication-pin-service";
import useCurrentNotebook from "utilities/hooks/domain/notebooks/use-current-notebook";

/**
 * Utility hook to manage pinning/unpinning publications.
 * @param publication
 * @param pins
 * @param onChangePins
 * @param onSuccess
 */
export default function useUpdatePublicationPins(
    publication: PublicationRecord,
    pins: Array<UserPublicationPinRecord>,
    onChangePins: (newValue: Array<UserPublicationPinRecord>) => void,
    onSuccess?: (isPinned: boolean) => void
) {
    const { globalState } = useGlobalState();
    const { currentNotebook } = useCurrentNotebook();

    const { currentIdentity } = globalState;

    const { t } = useLocalization();

    const [loading, setLoading] = useState(false);
    const pin = useMemo(
        () =>
            pins.find(
                (f: UserPublicationPinRecord) =>
                    f.publicationCode === publication.code &&
                    f.publicationEdition === publication.edition
            ),
        [pins, publication]
    );
    const isPinned = useMemo(() => pin != null, [pin]);
    const currentUserId = useMemo(
        () => currentIdentity?.userId() ?? 0,
        [currentIdentity]
    );

    const currentNotebookId = useMemo(
        () => currentNotebook?.id,
        [currentNotebook?.id]
    );

    const { delete: deletePinApi } = UserPublicationPinService.useDelete();
    const { create: createPinApi } = UserPublicationPinService.useCreate();

    const addPin = async () => {
        // already pinned or publication not loaded yet
        if (
            pin != null ||
            !publication.isPersisted() ||
            currentNotebookId == null
        ) {
            return;
        }

        await Do.try(async () => {
            setLoading(true);
            const model = new UserPublicationPinRecord().with({
                userId: currentUserId,
                publicationCode: publication.code ?? "",
                publicationEdition: publication.edition ?? "",
                publication: publication,
            });
            const result = await createPinApi(model, {
                userId: currentUserId,
                notebookId: currentNotebookId,
            });
            onChangePins([result.resultObject!, ...pins]);
            onSuccess?.(true);
        })
            .catch(() =>
                ToastManager.error(
                    t("errors-actionResource", {
                        action: t("creating"),
                        resource: t("pin"),
                    })
                )
            )
            .finally(() => setLoading(false))
            .getAwaiter();
    };

    const unpin = async () => {
        // already not pin or publication not loaded yet
        if (pin == null || !publication.isPersisted()) {
            return;
        }

        await Do.try(async () => {
            setLoading(true);
            const id = pin.id!;
            await deletePinApi(id, {
                userId: currentUserId,
                id,
                notebookId: currentNotebookId,
            });
            onChangePins(
                pins.filter((f: UserPublicationPinRecord) => f.id !== id)
            );
            onSuccess?.(false);
        })
            .catch(() =>
                ToastManager.error(
                    t("errors-actionResource", {
                        action: t("deleting"),
                        resource: t("pin"),
                    })
                )
            )
            .finally(() => setLoading(false))
            .getAwaiter();
    };

    const togglePin = async () => {
        return isPinned ? await unpin() : await addPin();
    };

    return {
        addPin,
        unpin,
        togglePin,
        isPinned,
        loading,
    };
}
